import { type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardSummary } from '@neuroedge/shared';
import { Card, PageState } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useDashboardSummary } from '../../api/reports';

function StatCard({
  label,
  value,
  to,
  tone,
}: {
  label: string;
  value: ReactNode;
  to?: string;
  tone?: 'danger' | 'success';
}) {
  const body = (
    <>
      <div className="ne-stat__value" style={tone ? { color: `var(--color-${tone})` } : undefined}>
        {value}
      </div>
      <div className="ne-stat__label">{label}</div>
    </>
  );
  return to ? (
    <Link to={to} className="ne-stat ne-stat--link">
      {body}
    </Link>
  ) : (
    <div className="ne-stat">{body}</div>
  );
}

const ACCENT: Record<string, string> = {
  leads: 'var(--color-primary)',
  conversations: 'var(--color-accent-blue)',
  tasks: 'var(--color-accent-amber)',
  orders: 'var(--color-accent-purple)',
  appointments: 'var(--color-accent-teal)',
  quotations: 'var(--color-accent-purple)',
  channels: 'var(--color-text-muted)',
};

function TileGroup({
  title,
  accent,
  children,
}: {
  title: string;
  accent: keyof typeof ACCENT;
  children: ReactNode;
}) {
  return (
    <section className="ne-tile-group" style={{ '--tile-accent': ACCENT[accent] } as CSSProperties}>
      <div className="ne-tile-group__head">
        <span className="ne-tile-group__dot" />
        <h2 className="ne-tile-group__title">{title}</h2>
      </div>
      <div className="ne-stat-grid">{children}</div>
    </section>
  );
}

function StageBar({
  stage,
  maxCount,
}: {
  stage: DashboardSummary['leads']['byStage'][number];
  maxCount: number;
}) {
  const pct = maxCount > 0 ? Math.round((stage.count / maxCount) * 100) : 0;
  return (
    <div className="ne-stagebar">
      <div className="ne-stagebar__head">
        <span>{stage.stageName}</span>
        <span style={{ color: 'var(--color-text-muted)' }}>{stage.count}</span>
      </div>
      <div className="ne-stagebar__track">
        <div className="ne-stagebar__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const SUBSCRIPTION_LABEL: Record<string, string> = {
  TRIALING: 'Trialing',
  ACTIVE: 'Active',
  PAST_DUE: 'Past due',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

function BillingCard({
  billing,
  usage,
}: {
  billing: DashboardSummary['billing'];
  usage: DashboardSummary['usage'];
}) {
  if (!billing.planName) {
    return (
      <Card title="License & billing">
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>No active plan.</p>
      </Card>
    );
  }
  const pct = usage.allowanceUsedPct;
  const overAllowance = pct !== null && pct >= 100;
  return (
    <Card title="License & billing">
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontWeight: 600 }}>{billing.planName}</span>
          <span
            className="ne-badge"
            style={
              billing.status === 'ACTIVE'
                ? { borderColor: 'var(--color-success)', color: 'var(--color-success)' }
                : billing.status === 'PAST_DUE' || billing.status === 'EXPIRED'
                  ? { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }
                  : undefined
            }
          >
            {SUBSCRIPTION_LABEL[billing.status ?? ''] ?? billing.status}
          </span>
        </div>
        {billing.renewsAt ? (
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
            Renews {new Date(billing.renewsAt).toLocaleDateString()}
          </div>
        ) : null}
        {billing.messageAllowance !== null ? (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              <span>Message usage this month</span>
              <span
                style={{ color: overAllowance ? 'var(--color-danger)' : 'var(--color-text-muted)' }}
              >
                {usage.messagesThisMonth.toLocaleString()} /{' '}
                {billing.messageAllowance.toLocaleString()}
                {pct !== null ? ` (${pct}%)` : ''}
              </span>
            </div>
            <div className="ne-stagebar__track">
              <div
                className="ne-stagebar__fill"
                style={{
                  width: `${Math.min(pct ?? 0, 100)}%`,
                  background: overAllowance ? 'var(--color-danger)' : undefined,
                }}
              />
            </div>
          </div>
        ) : null}
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Template messages sent this month: {usage.templateMessagesThisMonth.toLocaleString()}
        </div>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const { user, hasPermission } = useAuth();
  const canSeeBilling = hasPermission('billing.read');
  const canSeeOrders = hasPermission('products.read');
  const { data, isPending, isError, error, refetch } = useDashboardSummary();

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Dashboard</h1>
        <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
          Welcome back{user?.displayName ? `, ${user.displayName}` : ''}. Here's what's happening.
        </p>
      </div>

      {isPending ? (
        <PageState kind="loading" message="Loading dashboard…" />
      ) : isError ? (
        <PageState
          kind="error"
          message={`Could not load dashboard data (${error.message}).`}
          onRetry={() => void refetch()}
        />
      ) : (
        <>
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <TileGroup title="Leads" accent="leads">
              <StatCard label="Open leads" value={data.leads.openTotal} to="/leads" />
              <StatCard label="New leads this week" value={data.leads.newThisWeek} to="/leads" />
              <StatCard
                label="Won this month"
                value={data.leads.wonThisMonth}
                tone="success"
                to="/leads"
              />
            </TileGroup>

            <TileGroup title="Conversations" accent="conversations">
              <StatCard
                label="Open conversations"
                value={data.conversations.open}
                to="/conversations?filter=open"
              />
              <StatCard
                label="Unread messages"
                value={data.conversations.unread}
                to="/conversations?filter=unread"
                tone={data.conversations.unread > 0 ? 'danger' : undefined}
              />
            </TileGroup>

            <TileGroup title="Tasks & follow-ups" accent="tasks">
              <StatCard
                label="Tasks due today"
                value={data.tasks.dueToday}
                to="/tasks"
                tone={data.tasks.dueToday > 0 ? 'danger' : undefined}
              />
              <StatCard
                label="Overdue tasks"
                value={data.tasks.overdue}
                to="/tasks"
                tone={data.tasks.overdue > 0 ? 'danger' : undefined}
              />
              <StatCard
                label="Follow-ups due today"
                value={data.tasks.followUpsDueToday}
                to="/follow-ups"
                tone={data.tasks.followUpsDueToday > 0 ? 'danger' : undefined}
              />
              <StatCard
                label="Overdue follow-ups"
                value={data.tasks.followUpsOverdue}
                to="/follow-ups"
                tone={data.tasks.followUpsOverdue > 0 ? 'danger' : undefined}
              />
              <StatCard
                label="Follow-ups due soon"
                value={data.tasks.followUpsUpcoming}
                to="/follow-ups"
              />
            </TileGroup>

            {canSeeOrders ? (
              <TileGroup title="Orders" accent="orders">
                <StatCard
                  label="Pending orders"
                  value={data.orders.pending}
                  to="/orders"
                  tone={data.orders.pending > 0 ? 'danger' : undefined}
                />
                <StatCard
                  label="Orders placed today"
                  value={data.orders.placedToday}
                  to="/orders"
                />
                <StatCard
                  label="Completed orders today"
                  value={data.orders.completedToday}
                  to="/orders"
                  tone="success"
                />
              </TileGroup>
            ) : null}

            {canSeeOrders ? (
              <TileGroup title="Appointments" accent="appointments">
                <StatCard
                  label="Today's appointments"
                  value={data.appointments.today}
                  to="/appointments"
                />
              </TileGroup>
            ) : null}

            {canSeeOrders ? (
              <TileGroup title="Quotations" accent="quotations">
                <StatCard
                  label="Quotations today"
                  value={data.quotations.generatedToday}
                  to="/quotations"
                />
                <StatCard
                  label="Quotations this month"
                  value={data.quotations.generatedThisMonth}
                  to="/quotations"
                />
                <StatCard
                  label="AI photos processed this month"
                  value={data.quotations.aiImagesProcessedThisMonth}
                  to="/quotations"
                />
                <StatCard
                  label="AI success rate"
                  value={
                    data.quotations.aiSuccessRatePct === null
                      ? '—'
                      : `${data.quotations.aiSuccessRatePct}%`
                  }
                  to="/quotations"
                  tone={
                    data.quotations.aiSuccessRatePct !== null &&
                    data.quotations.aiSuccessRatePct < 70
                      ? 'danger'
                      : undefined
                  }
                />
              </TileGroup>
            ) : null}

            <TileGroup title="Channels" accent="channels">
              <StatCard
                label="Connected channels"
                value={`${data.channels.connected} / ${data.channels.total}`}
                to="/channels"
              />
            </TileGroup>
          </div>

          {data.leads.byStage.length || canSeeBilling ? (
            <div
              style={{
                display: 'grid',
                gap: 'var(--space-5)',
                gridTemplateColumns:
                  data.leads.byStage.length && canSeeBilling
                    ? 'minmax(0, 1.4fr) minmax(0, 1fr)'
                    : '1fr',
              }}
            >
              {data.leads.byStage.length ? (
                <Card title="Pipeline distribution (open leads)">
                  <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                    {data.leads.byStage.map((stage) => (
                      <StageBar
                        key={stage.stageId}
                        stage={stage}
                        maxCount={Math.max(...data.leads.byStage.map((s) => s.count), 1)}
                      />
                    ))}
                  </div>
                </Card>
              ) : null}
              {canSeeBilling ? <BillingCard billing={data.billing} usage={data.usage} /> : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
